<template>
  <div>
    <article class="project-analysis-item__wrapper">
      <AnalysisSettings
        :propReadOnly="propReadOnly"
        ref="AnalysisSettings"
        :statusDisableActionBtns="statusDisableActionBtns"
        :index="index"
        :thisAnalysisName="performanceAnalysisTitle"
        :isPerformanceAnalysis="true"
        :isSegmented="isSegmented"
        :sortOption="this.sortSetting"
        :filterParams="performanceAnalysisObject.filter_params"
        :savedThreshold="this.performanceAnalysisObject.threshold"
        :savedNodes="
          this.performanceAnalysisObject.saved_node_ids
            ? this.performanceAnalysisObject.saved_node_ids
            : []
        "
        :contentChanged="this.contentChanged"
        :hideSave="true"
        :lastModified="
          formatDateTime(performanceAnalysisObject.last_modified_on, $store.getters.getLanguage)
        "
        @toggled="isExpanded = !isExpanded"
        @changeAnalysisTitle="renameAnalysis"
      ></AnalysisSettings>
      <div v-if="isLoadingChartData">
        <Spinner :is-loading="isLoadingChartData" message="Refreshing chart" complete-message="" />
      </div>
      <div v-else>
        <div style="display: flex; flex-direction: row">
          <PerformanceCharts
            style="width: 100%"
            v-if="isExpanded"
            :chart-data="filteredChartData"
            :savedThreshold="this.performanceAnalysisObject.threshold"
            :isLoading="isLoadingChartData"
            :isSegmented="isSegmented"
            :performanceMode="
              this.performanceAnalysisObject.mode ? performanceAnalysisObject.mode : 'means'
            "
            :sentimentMode="
              this.performanceAnalysisObject.sentimentMode
                ? this.performanceAnalysisObject.sentimentMode
                : ''
            "
          />
          <InsightsFeed
            v-if="isExpanded"
            :chart_insights="
              performanceAnalysisObject.chart_insights
                ? performanceAnalysisObject.chart_insights
                : []
            "
          />
          <FindInsightsButton
            v-if="
              isExpanded &&
              (!performanceAnalysisObject.chart_insights ||
                !performanceAnalysisObject.chart_insights.length)
            "
            style="margin-right: 1rem"
            @refresh_for_insights="simpleRefresh"
          />
        </div>
      </div>
    </article>
  </div>
</template>

<script>
import PerformanceCharts from './Components/PerformanceCharts.vue';
import AnalysisSettings from '@/components/Project/ProjectAnalysis/Components/AnalysisSettings.vue';
import InsightsFeed from '@/components/Project/ProjectAnalysis/Components/InsightsFeed.vue';
import FindInsightsButton from '@/components/Project/ProjectAnalysis/Components/FindInsightsButton.vue';

// Helpers
import moment from 'moment';
import _ from 'lodash';

import ProjectMixin from '@/components/Project/Mixins/projectMixin.js';
import ProjectReportMixin from '@/components/Project/Mixins/projectReportMixin.js';
import FiltersMixin from '@/utils/mixins/userMixin.js';
import AnalysisMixin from '@/components/Project/ProjectAnalysis/Components/analysisMixin.js';
import PerformanceAnalysisMixin from '@/components/Project/ProjectAnalysis/ProjectAnalysisPerformance/Mixins/performanceAnalysisMixin.js';

export default {
  name: 'ProjectAnalysisPerformanceItem',
  components: {
    AnalysisSettings,
    PerformanceCharts,
    InsightsFeed,
    FindInsightsButton,
  },
  mixins: [ProjectMixin, ProjectReportMixin, FiltersMixin, PerformanceAnalysisMixin, AnalysisMixin],
  props: {
    index: {
      type: Number,
      required: true,
    },
    dataset: {
      type: Object,
      default: () => {},
    },
    retrievedSummaryTable: {
      type: Array,
      default: () => [],
    },
    propReadOnly: {
      type: Boolean,
      default: () => false,
    },
    expandThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    renameThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    saveThisAnalysis: {
      default: () => false,
      type: Boolean,
    },
    slideName: {
      default: () => '',
      type: String,
    },
    slideForSaving: {
      default: () => {},
      type: Object,
    },
    setToUpdatePerformanceMode: {
      default: () => false,
      type: Boolean,
    },
    selectedPerformanceMode: {
      default: () => 'Means/averages',
      type: String,
    },
    setToUpdatePerformanceSort: {
      default: () => false,
      type: Boolean,
    },
    selectedPerformanceSort: {
      default: () => 'Original',
      type: String,
    },
    setToUpdatePerformanceTopBox: {
      default: () => false,
      type: Boolean,
    },
    selectedPerformanceTopBox: {
      default: () => -1,
      type: Number,
    },
    setToUpdateTimeFilter: {
      default: () => false,
      type: Boolean,
    },
    selectedTimeFilter: {
      default: () => 'All data',
      type: String,
    },
    selectedTimeSeries: {
      default: () => 'Days',
      type: String,
    },
    setToUpdateTimeSeries: {
      default: () => false,
      type: Boolean,
    },
    setToUpdateSelectedTextForSentiment: {
      default: () => false,
      type: Boolean,
    },
    selectedTextForSentiment: {
      default: () => '',
      type: String,
    },
    setToUpdateSelectedSentimentMode: {
      default: () => false,
      type: Boolean,
    },
    sentimentMode: {
      default: () => '',
      type: String,
    },
  },
  data() {
    return {
      savedSlide: '',
      isExpanded: false,
      contentChanged: false,
      performanceAnalysisObject: {},
      filteredChartData: [],
      isLoadingChartData: false,
    };
  },
  computed: {
    statusDisableActionBtns() {
      return this.isLoadingChartData;
    },
    isSegmented() {
      let segmented = '';
      if (this.performanceAnalysisObject.summary_table) {
        for (let i = 0; i < this.performanceAnalysisObject.summary_table.length; i++) {
          if (
            this.performanceAnalysisObject.summary_table[i][1]['client_question_analysis'] &&
            this.performanceAnalysisObject.summary_table[i][1]['client_question_analysis']
              .unique_segments_count_dict
          ) {
            segmented =
              this.performanceAnalysisObject.summary_table[i][1]['client_question_analysis']
                .unique_segments_count_dict[0].question_title;
          } else if (
            this.performanceAnalysisObject.summary_table[i]['client_question_analysis'] &&
            this.performanceAnalysisObject.summary_table[i]['client_question_analysis']
              .unique_segments_count_dict
          ) {
            segmented =
              this.performanceAnalysisObject.summary_table[i]['client_question_analysis']
                .unique_segments_count_dict[0].question_title;
          }
        }
      }
      return segmented;
    },
    sortSetting() {
      if (this.performanceAnalysisObject && this.performanceAnalysisObject.sortedBy) {
        return this.performanceAnalysisObject.sortedBy;
      } else {
        return '';
      }
    },
    setThreshold() {
      if (this.performanceAnalysisObject && this.performanceAnalysisObject.threshold > -1) {
        return this.performanceAnalysisObject.threshold;
      } else {
        return -1;
      }
    },
    performanceAnalysisTitle() {
      let performanceTitle = '';
      if (this.performanceAnalysisObject) {
        if (
          this.performanceAnalysisObject.analysis_title &&
          this.performanceAnalysisObject.analysis_title.length
        ) {
          performanceTitle = this.performanceAnalysisObject.analysis_title;
        }
      }
      return performanceTitle;
    },
  },
  async created() {
    this.cloneSummaryTable();

    // if (
    //   !this.performanceAnalysisObject.analysis_title ||
    //   !this.performanceAnalysisObject.analysis_title.length
    // ) {
    //   let performanceTitle = '';
    //   if (this.performanceAnalysisObject.selectedQuestions.length) {
    //     performanceTitle = 'Across ' + this.performanceAnalysisObject.selectedQuestions.length;
    //     if (this.performanceAnalysisObject.selectedQuestions.length == 1) {
    //       performanceTitle =
    //         'Performance across ' +
    //         this.performanceAnalysisObject.selectedQuestions.length +
    //         ' scale question';
    //     } else {
    //       performanceTitle =
    //         'Performance across ' +
    //         this.performanceAnalysisObject.selectedQuestions.length +
    //         ' scale questions';
    //     }
    //   }

    //   if (performanceTitle.length) {
    //     this.renameAnalysis(performanceTitle);
    //   }
    //}
    if (this.performanceAnalysisObject.sortedBy) {
      this.$emit('updateSortOptions', this.performanceAnalysisObject.sortedBy);
    }
    if (this.performanceAnalysisObject['threshold']) {
      this.$emit('updateTopBoxOptions', this.performanceAnalysisObject.threshold);
    }
    if (this.performanceAnalysisObject.sentimentMode) {
      this.$emit('updateSentimentMode', this.performanceAnalysisObject.sentimentMode);
    }
    await this.checkForOldSavedNodes(); // Prior to 1.10.0, old nodes were saved directly, wasting space. Revert back to IDs
  },
  methods: {
    // In legacy objects, saved_node_ids were actually full nodes, wasteful. Revert back to ids. These are just used by AnalysisSettings to recolor
    async checkForOldSavedNodes() {
      if (
        this.performanceAnalysisObject.saved_node_ids &&
        this.performanceAnalysisObject.saved_node_ids.length
      ) {
        let new_ids = [];
        for (let i = 0; i < this.performanceAnalysisObject.saved_node_ids.length; i++) {
          if (
            Object.keys(this.performanceAnalysisObject.saved_node_ids[i]).length &&
            this.performanceAnalysisObject.saved_node_ids[i].uuid
          ) {
            new_ids.push(this.performanceAnalysisObject.saved_node_ids[i].uuid);
          }
        }
        if (new_ids.length) {
          this.performanceAnalysisObject.saved_node_ids = new_ids;
          this.$emit('refresh');
        }
      }
    },
    async renameAnalysis(title) {
      if (this.propReadOnly) return;
      this.performanceAnalysisObject.analysis_title = title;
      const indx = this.project.performanceAnalysis.findIndex(
        (d) => d.uuid == this.performanceAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.performanceAnalysis[indx].analysis_title = title;
        this.project.performanceAnalysis[indx].last_modified_on = moment().valueOf();
        await this.cleanAndSaveProject();
      }
    },
    cloneSummaryTable() {
      this.performanceAnalysisObject = _.cloneDeep(this.project.performanceAnalysis[this.index]);
      this.updateFilteredChartData();
    },
    updateSavedSlide(savedSlide) {
      this.savedSlide = savedSlide;
    },
    async refreshChartData() {
      let datasetID = this.dataset._id.$oid;
      let clientQuestionIds = this.performanceAnalysisObject.selectedQuestions.map(
        (question) => question[0],
      );
      let segmentedBy = '';
      if (this.performanceAnalysisObject.segmentedBy) {
        segmentedBy = this.performanceAnalysisObject.segmentedBy;
      }
      let filterParams = {};
      if (this.performanceAnalysisObject.filter_params) {
        filterParams = this.performanceAnalysisObject.filter_params;
      }
      let uuid = this.performanceAnalysisObject.uuid;
      let times_series = '';
      if (this.performanceAnalysisObject.time_series) {
        times_series = this.performanceAnalysisObject.time_series;
      }
      let selected_text_for_sentiment = '';
      if (this.performanceAnalysisObject.selected_text_for_sentiment) {
        selected_text_for_sentiment = this.performanceAnalysisObject.selected_text_for_sentiment;
      }
      const response = await this.$_performanceAnalysisMixin_fetchAnalysis(
        datasetID,
        clientQuestionIds,
        segmentedBy,
        filterParams,
        uuid,
        times_series,
        selected_text_for_sentiment,
      );
      if (response && typeof response === 'object') {
        response.summary_table = Object.entries(response.summary_table);
        this.performanceAnalysisObject.summary_table = response.summary_table;
        this.performanceAnalysisObject.chart_insights = []; // Until we auto-generate, erase
      }
    },
    async changeSortOptions(newSortOption) {
      if (!newSortOption) return;
      this.performanceAnalysisObject.sortedBy = newSortOption;
      await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);
    },
    async changePerformanceMode(performanceMode) {
      if (!performanceMode) return;
      this.isLoadingChartData = true;
      // Check for possible renaming
      if (
        this.performanceAnalysisObject.mode &&
        this.performanceAnalysisObject.mode != performanceMode
      ) {
        let newModeLabel = '';
        if (performanceMode == 'Means/averages') {
          newModeLabel = 'Performance across';
          if (this.performanceAnalysisObject.analysis_title.includes('Volumes across')) {
            this.performanceAnalysisObject.analysis_title =
              this.performanceAnalysisObject.analysis_title.replace(
                /^Volumes across/,
                newModeLabel,
              );
          }
          if (this.performanceAnalysisObject.analysis_title.includes('Sentiment across')) {
            this.performanceAnalysisObject.analysis_title =
              this.performanceAnalysisObject.analysis_title.replace(
                /^Sentiment across/,
                newModeLabel,
              );
          }
        } else if (performanceMode == 'Volumes') {
          newModeLabel = 'Volumes across';

          // Also remove top-box if it is set
          if (this.performanceAnalysisObject.threshold > -1) {
            this.performanceAnalysisObject.threshold = -1;
            this.$emit('updateTopBoxOptions', -1);
          }

          if (this.performanceAnalysisObject.analysis_title.includes('Performance across')) {
            this.performanceAnalysisObject.analysis_title =
              this.performanceAnalysisObject.analysis_title.replace(
                /^Performance across/,
                newModeLabel,
              );
          }
          if (this.performanceAnalysisObject.analysis_title.includes('Sentiment across')) {
            this.performanceAnalysisObject.analysis_title =
              this.performanceAnalysisObject.analysis_title.replace(
                /^Sentiment across/,
                newModeLabel,
              );
          }
        }
      }
      this.performanceAnalysisObject.mode = performanceMode;
      await this.refreshChartData();
      await this.updateFilteredChartData(this.performanceAnalysisObject.sortedBy);
      await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);
      this.isLoadingChartData = false;
    },
    async changeTimeSeries(timeSeries) {
      if (!timeSeries) return;
      this.isLoadingChartData = true;
      this.performanceAnalysisObject.time_series = timeSeries;
      // currently we only visualize compound scores, so if time series is activated, revert sentiment mode
      if (
        timeSeries !== 'None' &&
        this.performanceAnalysisObject.sentimentMode == '5-point scale'
      ) {
        this.performanceAnalysisObject.sentimentMode == 'Single score';
        await this.changeSentimentMode('Single score');
        this.$emit('updateSentimentMode', 'Single score');
        this.isLoadingChartData = true;
      }
      await this.refreshChartData();
      await this.updateFilteredChartData(this.performanceAnalysisObject.sortedBy);
      await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);
      this.isLoadingChartData = false;
    },
    async enableSentimentMode(text_question) {
      this.isLoadingChartData = true;

      if (text_question !== this.performanceAnalysisObject.selected_text_for_sentiment) {
        // Check for possible renaming
        if (text_question) {
          if (this.performanceAnalysisObject.analysis_title.includes('Volumes across')) {
            this.performanceAnalysisObject.analysis_title =
              this.performanceAnalysisObject.analysis_title.replace(
                /^Volumes across/,
                'Sentiments across',
              );
          }

          // currently we only visualize compound scores, so just in case, if time series automatically apply this mode
          if (this.performanceAnalysisObject.time_series !== 'None') {
            this.performanceAnalysisObject.sentimentMode == 'Single score';
            await this.changeSentimentMode('Single score');
            this.$emit('updateSentimentMode', 'Single score');
            this.isLoadingChartData = true;
          }
        } else {
          if (this.performanceAnalysisObject.analysis_title.includes('Sentiments across')) {
            this.performanceAnalysisObject.analysis_title =
              this.performanceAnalysisObject.analysis_title.replace(
                /^Sentiments/,
                'Volumes across',
              );
          }
        }
      }

      this.performanceAnalysisObject.selected_text_for_sentiment = text_question;

      await this.refreshChartData();
      await this.updateFilteredChartData(this.performanceAnalysisObject.sortedBy);
      await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);
      this.isLoadingChartData = false;
    },

    async changeSentimentMode(sentiment_mode) {
      this.isLoadingChartData = true;
      this.performanceAnalysisObject.sentimentMode = sentiment_mode;
      this.performanceAnalysisObject.chart_insights = [];
      await this.updateFilteredChartData(this.performanceAnalysisObject.sortedBy);
      await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);

      this.isLoadingChartData = false;
    },

    async changeTimeFilter(timeFilter) {
      if (!timeFilter) return;
      if (this.dataset.created_on_column_id) {
        let question_id = this.dataset.created_on_column_id;
        let filter_params = {};
        if (
          this.performanceAnalysisObject.filter_params &&
          Object.keys(this.performanceAnalysisObject.filter_params).length &&
          this.performanceAnalysisObject.filter_params['operands']
        ) {
          filter_params = this.deepCloneObj(this.performanceAnalysisObject.filter_params);
        } else {
          filter_params = {
            client_question_id: question_id,
            operands: [],
            global_combine_action: 'and',
          };
        }
        const new_filter_params = await this.$_analysisMixin_calculateNewTimeFilters(
          filter_params,
          timeFilter,
        );
        this.isLoadingChartData = true;
        this.performanceAnalysisObject.filter_params = new_filter_params;
        this.performanceAnalysisObject.time_filter_name = timeFilter;
        await this.refreshChartData();
        await this.updateFilteredChartData(this.performanceAnalysisObject.sortedBy);
        await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);
        this.isLoadingChartData = false;
      }
    },

    async applyChartThreshold(threshold) {
      if (threshold < -1) return;
      this.performanceAnalysisObject.threshold = parseInt(threshold);
      await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);
    },
    // Currently unavailable, could be interesting to revive one day. Chart types are detected from chart data automatically now
    // async changeChart(chartType) {
    //   if (chartType == 'Horizontal Bar') {
    //     this.performanceAnalysisObject.chartType = 'HorizontalBarChart';
    //   } else if (chartType == 'Vertical Bar') {
    //     this.performanceAnalysisObject.chartType = 'VerticalBarChart';
    //   }
    //   await this.savePerformanceToProject(this.performanceAnalysisObject.uuid);
    // },

    async updateFilteredChartData(sortOption) {
      if (!sortOption) {
        sortOption = 'Original';
      }

      let performance_mode = 'Means/averages';
      if (this.performanceAnalysisObject.mode == 'Volumes') {
        performance_mode = 'Volumes';
      }

      if (this.sentimentMode) {
        this.performanceAnalysisObject.sentimentMode = this.sentimentMode;
      }

      if (this.performanceAnalysisObject.summary_table) {
        let storedData = this.deepCloneObj(this.performanceAnalysisObject.summary_table);
        let reorderedData = [];

        // Modify for segmented (which needs very little unless has time series)

        for (let i = 0; i < storedData.length; i++) {
          if (storedData[i][1]) {
            if (storedData[i][1]['client_question_analysis']) {
              let chartObject = {
                label: storedData[i][1]['client_question_analysis'].question_title,
              };

              if (storedData[i][1]['client_question_analysis']['unique_segments_count_dict']) {
                chartObject['unique_segments_count_dict'] =
                  storedData[i][1]['client_question_analysis']['unique_segments_count_dict'];
                for (let y = 0; y < chartObject['unique_segments_count_dict'].length; y++) {
                  if (chartObject['unique_segments_count_dict'][y]['time_series_scores']) {
                    for (
                      let a = 0;
                      a < chartObject['unique_segments_count_dict'][y]['time_series_scores'].length;
                      a++
                    ) {
                      let new_times_series_value = this.replaceWithThresold(
                        this.performanceAnalysisObject.threshold,
                        chartObject['unique_segments_count_dict'][y]['time_series_scores'][a][
                          'unique_counts'
                        ],
                      );
                      chartObject['unique_segments_count_dict'][y]['time_series_scores'][a][
                        'modified_mean'
                      ] = new_times_series_value;
                    }
                  }

                  chartObject['unique_segments_count_dict'][y]['other_segment_value'] =
                    storedData[i][1]['client_question_analysis'].question_title;
                }
              }

              if (storedData[i][1]['client_question_analysis']['time_series_scores']) {
                chartObject['time_series_scores'] =
                  storedData[i][1]['client_question_analysis']['time_series_scores'];
                if (
                  this.performanceAnalysisObject.threshold &&
                  this.performanceAnalysisObject.threshold > -1
                ) {
                  for (let y = 0; y < chartObject['time_series_scores'].length; y++) {
                    if (chartObject['time_series_scores'][y]['unique_counts']) {
                      let new_times_series_value = this.replaceWithThresold(
                        this.performanceAnalysisObject.threshold,
                        chartObject['time_series_scores'][y]['unique_counts'],
                      );
                      chartObject['time_series_scores'][y]['modified_mean'] =
                        new_times_series_value;
                    }
                  }
                }
              }
              if (storedData[i][1]['client_question_analysis']['unique_sentiments_count']) {
                chartObject['unique_sentiments_count'] =
                  storedData[i][1]['client_question_analysis'].unique_sentiments_count;
                chartObject['value'] = storedData[i][1]['client_question_analysis'].overall_mean;
                reorderedData.push(chartObject);
              } else if (performance_mode == 'Means/averages') {
                chartObject['unique_numbers_count_dict'] =
                  storedData[i][1]['client_question_analysis'].unique_numbers_count_dict;
                chartObject['value'] = storedData[i][1]['client_question_analysis'].overall_mean;
                if (
                  this.performanceAnalysisObject.threshold &&
                  this.performanceAnalysisObject.threshold > -1
                ) {
                  chartObject.value = this.replaceWithThresold(
                    this.performanceAnalysisObject.threshold,
                    chartObject.unique_numbers_count_dict,
                  );
                }
                if (chartObject) {
                  reorderedData.push(chartObject);
                }
              } else if (performance_mode == 'Volumes') {
                chartObject['unique_numbers_count_dict'] =
                  storedData[i][1]['client_question_analysis'].unique_numbers_count_dict;
                chartObject['value'] = storedData[i][1]['client_question_analysis'].valid_size;

                if (chartObject) {
                  reorderedData.push(chartObject);
                }
              }
            }
          }
        }

        if (reorderedData.length) {
          if (sortOption === 'Original') {
            // if (this.$refs.AnalysisSettings) {
            //   this.$refs.AnalysisSettings.toggleContent();
            // }
            this.filteredChartData = reorderedData;
          } else if (sortOption === 'Lowest to Highest') {
            reorderedData.sort((a, b) => {
              return a.value - b.value;
            });
            // if (this.$refs.AnalysisSettings) {
            //   this.$refs.AnalysisSettings.toggleContent();
            // }
            this.filteredChartData = reorderedData;
          } else if (sortOption === 'Highest to Lowest') {
            reorderedData.sort((b, a) => {
              return a.value - b.value;
            });
            // if (this.$refs.AnalysisSettings) {
            //   this.$refs.AnalysisSettings.toggleContent();
            // }
            this.filteredChartData = reorderedData;
          }
        }
      }
    },

    async simpleRefresh() {
      if (this.project.performanceAnalysis && this.project.performanceAnalysis.length) {
        this.performanceAnalysisObject = _.cloneDeep(this.project.performanceAnalysis[this.index]);
      }
    },

    async saveToReport(selectedSlide, newSlideName) {
      const uuid = await this.savePerformanceToReport(selectedSlide, newSlideName);
      // this is used to get all AnalysisSetting->SaveAnalysis to rerun collections
      if (!this.project.reportSaves) {
        this.project.reportSaves = 1;
      } else {
        this.project.reportSaves = this.project.reportSaves + 1;
      }
      await this.savePerformanceToProject(uuid);
      this.$emit('refresh');
    },
    async savePerformanceToReport(selectedSlide, newSlideName) {
      try {
        this.isLoadingChartData = true;

        let createdReportID = await this.createReportIfEmpty(selectedSlide, newSlideName); // the above function will do nothing under conditions of emptiness
        if (selectedSlide) {
          createdReportID = selectedSlide.id;
        }

        const returnObject = await this.updateReportWithPerformance(
          this.performanceAnalysisObject.uuid,
          createdReportID,
          this.performanceAnalysisObject,
        );
        this.isLoadingChartData = false;

        await this.$emit('updateSlides');
        this.onSuccessfulSave();
        let node_ids = returnObject.newNodes.map((node) => node.uuid);
        this.performanceAnalysisObject.saved_node_ids = node_ids;
        return returnObject.uuid;
      } catch (e) {
        throw new Error('ProjectAnalysisPerformance:savePerformanceToReport ');
      }
    },
    async savePerformanceToProject(uuid = null) {
      if (this.propReadOnly || !this.performanceAnalysisObject) return;
      if (!this.performanceAnalysisObject.selected_text_for_sentiment) {
        this.performanceAnalysisObject.selected_text_for_sentiment = '';
      }
      const newObject = {
        last_modified_on: moment().valueOf(),
        uuid: uuid,
        analysis_title: this.performanceAnalysisObject.analysis_title,
        filter_params: this.performanceAnalysisObject.filter_params,
        time_filter_name: this.performanceAnalysisObject.time_filter_name,
        time_series: this.performanceAnalysisObject.time_series,
        mode: this.performanceAnalysisObject.mode,
        selectedQuestions: this.performanceAnalysisObject.selectedQuestions,
        isCancelled: this.performanceAnalysisObject.isCancelled,
        isFetching: this.performanceAnalysisObject.isFetching,
        isLongWait: this.performanceAnalysisObject.isLongWait,
        sortedBy: this.performanceAnalysisObject.sortedBy,
        summary_table: this.performanceAnalysisObject.summary_table,
        segmentedBy: this.performanceAnalysisObject.segmentedBy,
        threshold: this.performanceAnalysisObject.threshold,
        chart_insights: this.performanceAnalysisObject.chart_insights,
        saved_node_ids: this.performanceAnalysisObject.saved_node_ids,
        selected_text_for_sentiment: this.performanceAnalysisObject.selected_text_for_sentiment,
        sentimentMode: this.performanceAnalysisObject.sentimentMode,
      };
      // find it in currently saved drivers, if there update, if not add it
      const indx = this.project.performanceAnalysis.findIndex(
        (d) => d.uuid == this.performanceAnalysisObject.uuid,
      );
      if (indx > -1) {
        this.project.performanceAnalysis[indx].saved_node_ids =
          this.performanceAnalysisObject.saved_node_ids;
        this.project.performanceAnalysis[indx].last_modified_on = moment().valueOf();
        // newObject.saved_node_ids = this.performanceAnalysisObject.saved_node_ids
        this.project.performanceAnalysis.splice(indx, 1, _.cloneDeep(newObject));
        await this.cleanAndSaveProject();
      } else {
        await this.cleanAndSaveProject();
      }
      // else {
      //   this.project.performanceAnalysis.unshift(_.cloneDeep(newObject))
      // }

      this.performanceAnalysisObject = _.cloneDeep(newObject);
    },
    /************
     *
     * Confirmation modals
     *
     ***********/
    onSuccessfulSave() {
      this.setConfirmText({
        btn: 'okay',
        title: 'Performance saved',
        message: 'Performance successfully saved to report.',
      });
      this.setConfirmType('success');
      this.setConfirmSourceComponent('performance');
      this.setConfirmIsVisible(true);
    },

    replaceWithThresold(threshold, unique_counts) {
      let keys = Object.keys(unique_counts);
      if (threshold > -1 && keys.length) {
        let countPositive = 0;
        let countNegative = 0;
        for (let x = 0; x < keys.length; x++) {
          let keyName = keys[x];
          if (parseFloat(keyName) > threshold) {
            countPositive = countPositive + parseFloat(unique_counts[keyName]);
          } else {
            countNegative = countNegative + parseFloat(unique_counts[keyName]);
          }
        }
        let newValue = (countPositive / (countPositive + countNegative)) * 100;
        return this.roundToTwo(newValue);
        // suffix = "%"
      } else {
        return '';
      }
    },
    roundToTwo(num) {
      return +(Math.round(num + 'e+2') + 'e-2');
    },
    formatDateTime: (value, language) => {
      let currentLang = '';
      if (language) {
        currentLang = language;
      }
      if (value) {
        if (currentLang === 'fr') {
          return moment(value).locale('fr').format('MMMM D, YYYY hh:mm A').toString();
        } else {
          return moment(value).format('MMMM D, YYYY hh:mm A').toString();
        }
      }
    },
  },

  watch: {
    sortSetting: async function (newVal) {
      if (newVal) {
        this.updateFilteredChartData(newVal);
      }
    },
    setThreshold: async function (newVal, oldVal) {
      if (oldVal && newVal) {
        if (oldVal !== newVal) {
          if (this.$refs.AnalysisSettings) {
            this.$refs.AnalysisSettings.toggleContent();
            let sortOption = 'Original';
            if (this.performanceAnalysisObject.sortedBy) {
              sortOption = this.performanceAnalysisObject.sortedBy;
            }
            this.updateFilteredChartData(sortOption);
          }
        }
      }
    },
    retrievedSummaryTable: function (newVal, oldVal) {
      if (oldVal && newVal) {
        if (oldVal !== newVal && newVal.length) {
          this.cloneSummaryTable();
        }
      }
    },
    expandThisAnalysis: async function (val) {
      if (val) {
        this.isExpanded = true;
      } else {
        this.isExpanded = false;
      }
    },
    renameThisAnalysis: async function (val) {
      if (val) {
        if (this.$refs.AnalysisSettings) {
          this.$refs.AnalysisSettings.toggleRename();
        }
      }
    },
    saveThisAnalysis: async function (val) {
      if (val) {
        this.saveToReport(this.slideForSaving, this.slideName);
      }
    },
    setToUpdatePerformanceMode: async function (val) {
      if (val) {
        await this.changePerformanceMode(this.selectedPerformanceMode);
        this.$emit('updatePerformanceMode', this.selectedPerformanceMode);
      }
    },
    setToUpdatePerformanceSort: async function (val) {
      if (val) {
        if (this.selectedPerformanceSort) {
          await this.changeSortOptions(this.selectedPerformanceSort);
          this.$emit('updateSortOptions', this.selectedPerformanceSort);
        }
      }
    },
    setToUpdatePerformanceTopBox: async function (val) {
      if (val) {
        await this.applyChartThreshold(this.selectedPerformanceTopBox);
        this.$emit('updateTopBoxOptions', this.selectedPerformanceTopBox);
      }
    },
    setToUpdateTimeFilter: async function (val) {
      if (val) {
        await this.changeTimeFilter(this.selectedTimeFilter);
        this.$emit('updateTimeFilter', this.selectedTimeFilter);
      }
    },
    setToUpdateTimeSeries: async function (val) {
      if (val) {
        await this.changeTimeSeries(this.selectedTimeSeries);
        this.$emit('updateTimeSeries', this.selectedTimeSeries);
      }
    },
    setToUpdateSelectedTextForSentiment: async function (val) {
      if (val) {
        await this.enableSentimentMode(this.selectedTextForSentiment);
        this.$emit('enableSentimentMode', this.selectedTextForSentiment);
      }
    },

    setToUpdateSelectedSentimentMode: async function (val) {
      if (val) {
        await this.changeSentimentMode(this.sentimentMode);
        this.$emit('updateSentimentMode', this.sentimentMode);
      }
    },
  },
};
</script>
